import { useEffect, useState } from "react";
import type {
  SuperAdminOrganizationDetail,
  UpdateOrganizationEntitlementRequest,
} from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Menu,
  SlidersHorizontal,
  Coins,
} from "lucide-react";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { cn } from "../../lib/utils.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import { UsageSettingsView } from "../settings/UsageSettingsPage.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { PlatformEntitlementMark } from "./PlatformEntitlementMark.js";
import { Switch } from "../../components/ui/switch/index.js";
import { SuperAdminLimitField, SuperAdminPlatformToggle } from "./super-admin-setting-rows.js";
import {
  entitlementDraftFromDetail,
  entitlementFormKey,
  readEntitlementDraft,
  type EntitlementDraft,
} from "./organization-entitlement-draft.js";
import { SuperAdminMessageBalanceSection } from "./SuperAdminMessageBalanceSection.js";
// The component is now imported correctly
// Replaced imports
import { OrganizationActivitySection } from "./SuperAdminOrgDetailActivity.js";
import { SuperAdminOrgDetailSidebar } from "./SuperAdminOrgDetailSidebar.js";
import { Sheet, SheetContent } from "../../components/ui/sheet/index.js";
import {
  WORKSPACE_INTERNAL_SIDEBAR_ASIDE_CLASS,
  WORKSPACE_INTERNAL_SIDEBAR_COLLAPSED_PX,
  WORKSPACE_INTERNAL_SIDEBAR_EXPANDED_PX,
} from "../../components/layout/workspace-internal-sidebar.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import { useWorkspaceSidebarCollapsedPreference } from "../../components/layout/use-workspace-sidebar-collapsed-preference.js";
import { WORKSPACE_SIDEBAR_COLLAPSED_KEYS } from "../../components/layout/workspace-sidebar-collapsed-preference.js";

export const SUPER_ADMIN_ORG_DETAIL_TABS = [
  "overview",
  "limits",
  "whatsapp",
  "activity",
] as const;

export type SuperAdminOrgDetailTab = (typeof SUPER_ADMIN_ORG_DETAIL_TABS)[number];

export function parseSuperAdminOrgDetailTab(
  searchParams: URLSearchParams,
): SuperAdminOrgDetailTab {
  const raw = searchParams.get("tab");
  if (raw === "limits" || raw === "whatsapp" || raw === "activity" || raw === "overview") {
    return raw;
  }
  return "overview";
}

export function OrganizationSuspendConsequences(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <ul
      className="m-0 grid list-disc gap-1 ps-5 text-sm text-muted"
      data-testid="super-admin-suspend-consequences"
    >
      <li>{t("superAdmin.detail.suspendDialog.actions")}</li>
      <li>{t("superAdmin.detail.suspendDialog.outbound")}</li>
      <li>{t("superAdmin.detail.suspendDialog.inbound")}</li>
    </ul>
  );
}

export function SuperAdminOrganizationDetailView({
  state,
  onRetry,
  onChangeStatus,
  onChangeBeta,
  onSaveEntitlements,
}: {
  state: SuperAdminLoadState<SuperAdminOrganizationDetail>;
  onRetry: () => void;
  onChangeStatus: (status: "ACTIVE" | "SUSPENDED") => Promise<void>;
  onChangeBeta: (isBetaOrganization: boolean) => Promise<void>;
  onSaveEntitlements: (patch: UpdateOrganizationEntitlementRequest) => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();
  const [collapsed, setCollapsed] = useWorkspaceSidebarCollapsedPreference(
    WORKSPACE_SIDEBAR_COLLAPSED_KEYS.superAdminSettings,
  );

  if (state.status === "loading") {
    return (
      <div
        className="h-48 animate-pulse rounded-xl border border-line bg-surface p-6 m-6"
        data-testid="super-admin-detail-loading"
        aria-busy="true"
      />
    );
  }

  if (state.status === "error") {
    return (
      <div className="p-6">
        <ProductErrorState
          testId="super-admin-detail-error"
          title={t("superAdmin.detail.loadErrorTitle")}
          description={t("superAdmin.detail.loadErrorBody")}
          onRetry={onRetry}
          retryLabel={t("superAdmin.retry")}
        />
      </div>
    );
  }

  const organization = state.data;
  const asideWidth = collapsed
    ? WORKSPACE_INTERNAL_SIDEBAR_COLLAPSED_PX
    : WORKSPACE_INTERNAL_SIDEBAR_EXPANDED_PX;
  
  // Determine current section from path
  const path = location.pathname;
  const isLimits = path.endsWith("/limits");
  const isWhatsApp = path.endsWith("/whatsapp");
  const isActivity = path.endsWith("/activity");

  return (
    <div className="flex h-full min-h-0 bg-canvas" data-testid="super-admin-detail">
      <aside
        className={WORKSPACE_INTERNAL_SIDEBAR_ASIDE_CLASS}
        style={{ width: asideWidth }}
        data-testid="super-admin-org-detail-sidebar"
      >
        <SuperAdminOrgDetailSidebar 
          organization={organization}
          collapsed={collapsed} 
          onCollapsedChange={setCollapsed} 
        />
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent
            side="start"
            className="flex h-full max-h-dvh w-[min(100%,18rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg"
            overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
            data-testid="super-admin-org-detail-mobile-nav"
          >
            <SuperAdminOrgDetailSidebar
              organization={organization}
              variant="drawer"
              collapsed={false}
              onNavigate={() => setMobileNavOpen(false)}
              onClose={() => setMobileNavOpen(false)}
            />
          </SheetContent>
        </Sheet>

        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-surface px-4 py-3.5">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 lg:hidden"
              onClick={() => setMobileNavOpen(true)}
              data-testid="super-admin-org-open-mobile-nav"
            >
              <Menu size={14} aria-hidden="true" />
            </Button>
            <span
              className="hidden lg:inline-grid size-9 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-ink"
              aria-hidden="true"
            >
              <Building2 size={16} />
            </span>
            <div className="grid min-w-0 gap-0.5">
              <div className="flex items-center gap-2">
                <h1 className="m-0 truncate text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
                  {organization.name}
                </h1>
                <Badge variant={organization.status === "ACTIVE" ? "success" : "warning"} className="px-1.5 py-0">
                  {t(`superAdmin.status.${organization.status}`)}
                </Badge>
                {organization.isBetaOrganization ? (
                  <Badge variant="warning" className="px-1.5 py-0" data-testid="super-admin-org-beta-badge">
                    {t("superAdmin.detail.betaBadge")}
                  </Badge>
                ) : null}
              </div>
              <p className="m-0 flex items-center gap-1.5 text-[0.75rem] text-muted">
                <span>{t("superAdmin.detail.id")} <LtrValue>#{organization.id}</LtrValue></span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <OrganizationStatusControl
              organization={organization}
              onChangeStatus={onChangeStatus}
            />
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
          <div className="w-full">
            {isLimits ? (
              <OrganizationEntitlementForm
                key={entitlementFormKey(organization)}
                organization={organization}
                onSave={onSaveEntitlements}
                onChangeBeta={onChangeBeta}
              />
            ) : isWhatsApp ? (
              <SuperAdminMessageBalanceSection organizationId={organization.id} />
            ) : isActivity ? (
              <OrganizationActivitySection organizationId={organization.id} active={true} />
            ) : (
              <OrganizationOverviewTab
                organization={organization}
                onRetry={onRetry}
                onChangeBeta={onChangeBeta}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OrganizationOverviewTab({
  organization,
  onRetry,
  onChangeBeta,
}: {
  organization: SuperAdminOrganizationDetail;
  onRetry: () => void;
  onChangeBeta: (isBetaOrganization: boolean) => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const basePath = `/super-admin/organizations/${organization.id}`;

  return (
    <div className="grid gap-5" data-testid="super-admin-org-panel-overview">
      <OrganizationBetaToggleCard organization={organization} onChangeBeta={onChangeBeta} />
      <section className="grid gap-3">
        <div>
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {t("superAdmin.detail.usage")}
          </h2>
          <p className="m-0 mt-0.5 text-xs text-muted">
            {t("superAdmin.detail.usageSubtitle")}
          </p>
        </div>
        <UsageSettingsView
          state={{ status: "ready", usage: organization.usage }}
          onRetry={onRetry}
        />
      </section>

      <section className="grid gap-3 rounded-xl border border-line bg-surface p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="m-0 text-sm font-semibold text-foreground">
              {t("superAdmin.detail.information")}
            </h2>
            <p className="m-0 mt-0.5 text-xs text-muted">
              {t("superAdmin.detail.informationSubtitle")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              asChild
              data-testid="super-admin-overview-goto-limits"
            >
              <Link to={`${basePath}/limits`}>
                <SlidersHorizontal size={13} className="me-1.5" aria-hidden="true" />
                {t("superAdmin.detail.quickActions.editLimits")}
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              asChild
              data-testid="super-admin-overview-goto-whatsapp"
            >
              <Link to={`${basePath}/whatsapp`}>
                <Coins size={13} className="me-1.5" aria-hidden="true" />
                {t("superAdmin.detail.quickActions.manageWhatsApp")}
              </Link>
            </Button>
          </div>
        </div>

        <dl className="m-0 grid grid-cols-1 gap-3 border-t border-line/70 pt-3.5 text-xs sm:grid-cols-3">
          <div className="rounded-lg border border-line/60 bg-surface-muted/35 p-3">
            <dt className="font-medium text-muted">{t("settings.general.timezoneLabel")}</dt>
            <dd className="m-0 mt-1 text-sm font-semibold text-foreground">
              <LtrValue>{organization.timezone}</LtrValue>
            </dd>
          </div>

          <div className="rounded-lg border border-line/60 bg-surface-muted/35 p-3">
            <dt className="font-medium text-muted">{t("superAdmin.detail.created")}</dt>
            <dd className="m-0 mt-1 text-sm font-semibold text-foreground">
              <LtrValue>{new Date(organization.createdAt).toLocaleDateString(i18n.language)}</LtrValue>
            </dd>
          </div>

          <div className="rounded-lg border border-line/60 bg-surface-muted/35 p-3">
            <dt className="font-medium text-muted">{t("superAdmin.detail.entitlements")}</dt>
            <dd className="m-0 mt-1.5 flex flex-wrap items-center gap-2.5">
              <PlatformEntitlementMark
                platform="whatsapp"
                enabled={organization.platformEntitlements.whatsappEnabled}
              />
              <PlatformEntitlementMark
                platform="facebook"
                enabled={organization.platformEntitlements.facebookEnabled}
              />
              <PlatformEntitlementMark
                platform="instagram"
                enabled={organization.platformEntitlements.instagramEnabled}
              />
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}

function OrganizationStatusControl({
  organization,
  onChangeStatus,
}: {
  organization: SuperAdminOrganizationDetail;
  onChangeStatus: (status: "ACTIVE" | "SUSPENDED") => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState<"suspend" | "reactivate" | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const suspended = organization.status === "SUSPENDED";

  async function applyStatus(): Promise<void> {
    if (!confirm) return;
    setPending(true);
    setFailed(false);
    try {
      await onChangeStatus(confirm === "suspend" ? "SUSPENDED" : "ACTIVE");
      setConfirm(null);
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      {suspended ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          data-testid="super-admin-reactivate"
          onClick={() => {
            setFailed(false);
            setConfirm("reactivate");
          }}
        >
          {t("superAdmin.detail.reactivate")}
        </Button>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 border-destructive/35 bg-transparent text-destructive hover:bg-error-muted"
          data-testid="super-admin-suspend"
          onClick={() => {
            setFailed(false);
            setConfirm("suspend");
          }}
        >
          {t("superAdmin.detail.suspend")}
        </Button>
      )}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (pending) return;
          if (!open) {
            setConfirm(null);
            setFailed(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {confirm === "suspend"
              ? t("superAdmin.detail.suspendDialog.title")
              : t("superAdmin.detail.reactivateDialog.title")}
          </AlertDialogTitle>
          {confirm === "suspend" ? (
            <>
              <AlertDialogDescription>
                {t("superAdmin.detail.suspendDialog.intro")}
              </AlertDialogDescription>
              <OrganizationSuspendConsequences />
            </>
          ) : (
            <AlertDialogDescription>
              {t("superAdmin.detail.reactivateDialog.body")}
            </AlertDialogDescription>
          )}
          {failed ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              {t("superAdmin.detail.statusError")}
            </p>
          ) : null}
          <AlertDialogActions>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pending}
              onClick={() => setConfirm(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={confirm === "suspend" ? "destructive" : "default"}
              disabled={pending}
              data-testid="super-admin-status-confirm"
              onClick={() => void applyStatus()}
            >
              {confirm === "suspend"
                ? t("superAdmin.detail.suspendDialog.confirm")
                : t("superAdmin.detail.reactivateDialog.confirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function OrganizationBetaToggleCard({
  organization,
  onChangeBeta,
}: {
  organization: SuperAdminOrganizationDetail;
  onChangeBeta: (isBetaOrganization: boolean) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [pending, setPending] = useState(false);
  const switchId = "super-admin-org-beta-toggle";

  async function apply(next: boolean): Promise<void> {
    if (next === organization.isBetaOrganization || pending) return;
    setPending(true);
    try {
      await onChangeBeta(next);
      notifications?.pushNotification({ message: t("superAdmin.detail.betaSaved") });
    } catch {
      notifications?.pushNotification({ message: t("superAdmin.detail.betaSaveError") });
    } finally {
      setPending(false);
    }
  }

  return (
    <section
      className="grid gap-3 rounded-xl border border-line bg-surface p-4 sm:p-5"
      data-testid="super-admin-org-beta-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="grid min-w-0 gap-0.5">
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {t("superAdmin.detail.betaOrganizationTitle")}
          </h2>
          <p className="m-0 text-xs leading-relaxed text-muted">
            {t("superAdmin.detail.betaOrganizationDescription")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-foreground" htmlFor={switchId}>
            {t("superAdmin.detail.betaOrganizationLabel")}
          </label>
          <Switch
            id={switchId}
            checked={organization.isBetaOrganization}
            disabled={pending}
            data-testid={switchId}
            onCheckedChange={(checked) => void apply(checked)}
          />
        </div>
      </div>
    </section>
  );
}

function OrganizationEntitlementForm({
  organization,
  onSave,
  onChangeBeta,
}: {
  organization: SuperAdminOrganizationDetail;
  onSave: (patch: UpdateOrganizationEntitlementRequest) => Promise<void>;
  onChangeBeta: (isBetaOrganization: boolean) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [draft, setDraft] = useState<EntitlementDraft>(() =>
    entitlementDraftFromDetail(organization),
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<"invalid" | "save" | null>(null);
  const draftResult = readEntitlementDraft(organization, draft);
  const unchanged = draftResult.ok && Object.keys(draftResult.patch).length === 0;

  function updateDraft(next: EntitlementDraft): void {
    setDraft(next);
    setError(null);
  }

  async function save(): Promise<void> {
    if (!draftResult.ok) {
      setError("invalid");
      return;
    }
    if (unchanged) {
      return;
    }
    setPending(true);
    setError(null);
    try {
      await onSave(draftResult.patch);
      notifications?.pushNotification({ message: t("superAdmin.detail.saved") });
    } catch {
      setError("save");
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      id="super-admin-entitlement-form"
      className="grid gap-5"
      data-testid="super-admin-entitlement-form"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <OrganizationBetaToggleCard organization={organization} onChangeBeta={onChangeBeta} />
      <section className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="m-0 text-sm font-semibold text-foreground">
              {t("superAdmin.detail.limits")}
            </h2>
            <p className="m-0 mt-0.5 text-xs text-muted">
              {t("superAdmin.detail.limitsSubtitle")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-xs text-muted">
              {!unchanged
                ? t("superAdmin.detail.unsavedChanges")
                : t("superAdmin.detail.allChangesSaved")}
            </span>
            <Button
              type="submit"
              form="super-admin-entitlement-form"
              size="sm"
              disabled={pending || unchanged}
              data-testid="super-admin-entitlements-save"
            >
              {pending ? t("superAdmin.detail.saving") : t("superAdmin.detail.saveEntitlements")}
            </Button>
          </div>
        </div>

        {error === "invalid" ? (
          <p
            className="m-0 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            {t("superAdmin.detail.invalidLimits")}
          </p>
        ) : null}
        {error === "save" ? (
          <p
            className="m-0 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            {t("superAdmin.detail.saveError")}
          </p>
        ) : null}

        <div
          className="overflow-hidden rounded-xl border border-line bg-surface"
          data-testid="super-admin-limits"
        >
          <SuperAdminLimitField
            id="super-admin-contacts-limit"
            label={t("settings.usage.contacts")}
            description={t("superAdmin.detail.contactsDescription")}
            unlimitedLabel={t("settings.usage.unlimited")}
            unlimited={draft.contactsUnlimited}
            value={draft.contactsValue}
            onUnlimitedChange={(unlimited) =>
              updateDraft({ ...draft, contactsUnlimited: unlimited })
            }
            onValueChange={(value) => updateDraft({ ...draft, contactsValue: value })}
          />
          <div className="h-px bg-line" />
          <SuperAdminLimitField
            id="super-admin-team-limit"
            label={t("settings.usage.team")}
            description={t("superAdmin.detail.teamDescription")}
            unlimitedLabel={t("settings.usage.unlimited")}
            unlimited={draft.teamUnlimited}
            value={draft.teamValue}
            onUnlimitedChange={(unlimited) =>
              updateDraft({ ...draft, teamUnlimited: unlimited })
            }
            onValueChange={(value) => updateDraft({ ...draft, teamValue: value })}
          />
          <div className="h-px bg-line" />
          <SuperAdminLimitField
            id="super-admin-storage-quota"
            label={t("settings.usage.storage")}
            description={t("superAdmin.detail.storageDescription")}
            unlimitedLabel={t("settings.usage.unlimited")}
            unlimited={draft.storageUnlimited}
            value={draft.storageValue}
            unit={t("superAdmin.detail.gigabyteUnit")}
            onUnlimitedChange={(unlimited) =>
              updateDraft({ ...draft, storageUnlimited: unlimited })
            }
            onValueChange={(value) =>
              updateDraft({ ...draft, storageValue: value, storageTouched: true })
            }
          />
        </div>
      </section>

      <section className="grid gap-3" data-testid="super-admin-detail-entitlements">
        <div>
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {t("superAdmin.detail.entitlements")}
          </h2>
          <p className="m-0 mt-0.5 text-xs text-muted">
            {t("superAdmin.detail.entitlementsHint")}
          </p>
        </div>
        <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface p-0">
          <SuperAdminPlatformToggle
            platform="whatsapp"
            enabled={draft.whatsappEnabled}
            onChange={(enabled) => updateDraft({ ...draft, whatsappEnabled: enabled })}
          />
          <SuperAdminPlatformToggle
            platform="facebook"
            enabled={draft.facebookEnabled}
            onChange={(enabled) => updateDraft({ ...draft, facebookEnabled: enabled })}
          />
          <SuperAdminPlatformToggle
            platform="instagram"
            enabled={draft.instagramEnabled}
            onChange={(enabled) => updateDraft({ ...draft, instagramEnabled: enabled })}
          />
        </ul>
      </section>
    </form>
  );
}

